import { IconButton, MenuItem, Stack, TextField } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ServiceWaitingPeriodType } from "@platform/openapi-types";
import { SERVICE_TYPE_LABELS } from "@platform/plan-grid-core";

import type { ServiceWaitingPeriod } from "@platform/plan-grid-core";
import type { ReactElement } from "react";

export const ALL_SERVICE_TYPES = Object.values(ServiceWaitingPeriodType);

/** The draft (string-backed) form of a service waiting period entry. */
export interface DraftServiceEntry {
  /** Stable key for React, since entries can be added and removed. */
  id: string;
  type: ServiceWaitingPeriodType | "";
  months: string;
}

export const makeDraftEntry = (
  type: ServiceWaitingPeriodType | "" = "",
  months = "",
): DraftServiceEntry => ({
  id: crypto.randomUUID(),
  type,
  months,
});

// Drops any entry whose type isn't a recognized service type — defensive
// against stale/malformed persisted data, since the array arrives untyped
// off the AG Grid row.
export const toDraftEntries = (
  periods: ServiceWaitingPeriod[],
): DraftServiceEntry[] =>
  periods
    .filter((period) => ALL_SERVICE_TYPES.includes(period.type))
    .map((period) => makeDraftEntry(period.type, String(period.months)));

export const isValidEntry = (entry: DraftServiceEntry): boolean => {
  const months = Number(entry.months);

  return (
    entry.type !== "" &&
    entry.months !== "" &&
    Number.isInteger(months) &&
    months >= 1 &&
    months <= 24
  );
};

/** Parses a draft entry into a persistable period, or null when invalid. */
export const parseValidEntry = (
  entry: DraftServiceEntry,
): ServiceWaitingPeriod | null => {
  if (!isValidEntry(entry) || entry.type === "") return null;

  return { type: entry.type, months: Number(entry.months) };
};

/** Service types not already claimed by another row, so each row offers a
 * distinct type — including the current row's own type. */
export const availableTypesForRow = (
  entries: DraftServiceEntry[],
  index: number,
): ServiceWaitingPeriodType[] => {
  const selectedByOtherRows = new Set(
    entries
      .filter((_, entryIndex) => entryIndex !== index)
      .map((entry) => entry.type),
  );

  return ALL_SERVICE_TYPES.filter((type) => !selectedByOtherRows.has(type));
};

interface ServiceWaitingPeriodsFieldProps {
  entries: DraftServiceEntry[];
  showErrors: boolean;
  onUpdateType: (index: number, type: ServiceWaitingPeriodType) => void;
  onUpdateMonths: (index: number, months: string) => void;
  onRemove: (index: number) => void;
}

/**
 * Controlled list of service-waiting-period entries (service type + months)
 * with add/remove. Rendered by `ServiceWaitingPeriodsCellEditor` only while
 * that dialog's Type dropdown is set to "Waiting Periods" and at least one
 * entry exists — the dropdown's "None" option, not an empty list here, is
 * what represents no waiting periods for this plan.
 */
export const ServiceWaitingPeriodsField = ({
  entries,
  showErrors,
  onUpdateType,
  onUpdateMonths,
  onRemove,
}: ServiceWaitingPeriodsFieldProps): ReactElement => (
  <>
    {entries.map((entry, index) => {
      const invalid = showErrors && !isValidEntry(entry);
      const availableTypes = availableTypesForRow(entries, index);

      return (
        <Stack key={entry.id} direction="row" spacing={1} alignItems="center">
          <TextField
            select
            size="small"
            label="Service Type"
            value={entry.type}
            onChange={(e) =>
              onUpdateType(index, e.target.value as ServiceWaitingPeriodType)
            }
            error={invalid}
            fullWidth
          >
            {availableTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {SERVICE_TYPE_LABELS[type] || type}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            type="number"
            label="Months"
            value={entry.months}
            onChange={(e) => onUpdateMonths(index, e.target.value)}
            error={invalid}
            inputProps={{ min: 1, max: 24, step: 1 }}
            sx={{ width: 110 }}
          />

          <IconButton
            size="small"
            aria-label="Remove service waiting period"
            onClick={() => onRemove(index)}
            color="error"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      );
    })}
  </>
);
